import cx from 'classnames';
import {evite} from 'evite';
import PropTypes from 'prop-types';
import {useInView} from 'react-intersection-observer';
import Skeleton from 'react-loading-skeleton';
import {useDispatch} from 'react-redux';
import {DEFAULT_CANVAS_HEIGHT, DEFAULT_CANVAS_WIDTH} from '~/common/canvas/constants';
import {isAllowedVideoFormat} from '~/common/fabric/utils';
import {useCachedMedia} from '~/common/hooks/useCachedMedia';
import {
  GALLERY_PHOTO_PREVIEW_INDEXED_DB_STORE_NAME,
  GALLERY_PHOTO_PREVIEW_MEDIA_ID,
} from '~/common/indexed-db/dexieStores';
import {favoriteTemplateThunk} from '~/GalleryPB/api/favorites';
import {isIn3AcrossABTest} from '~/GalleryPB/experiments';
import {FavoritesHeart} from '~/GalleryPB/FavoritesHeart';
import {useUyoOrDyo} from '~/GalleryPB/hooks/useUyoOrDyo';
import {GalleryAssetName} from '~/GalleryPB/utils';
import {Picture} from '~library/atoms/Picture';
import {Typography} from '~library/atoms/Typography';
import {Video} from '~library/atoms/Video';
import {useMatchQuery} from '~library/hooks/useMatchQuery';
import {TemplateTypeIndicator} from '~library/molecules/TemplateTypeIndicator';
import {QuickViewButton} from './components/QuickViewButton/QuickViewButton';

import 'react-loading-skeleton/dist/skeleton.css';
import './Template.sass';

export const TemplateDesign = (props) => {
  const {result, href, onTemplateClick, onClickQuickView, loading, screen, favorited} = props;
  const {ref, inView} = useInView({
    triggerOnce: true,
    rootMargin: '10px 0px',
    initialInView: loading === 'eager',
  });
  const uyoMaskAssetKeyLegacy =
    screen === 'mw'
      ? GalleryAssetName.desktop.uyo_mask_legacy
      : GalleryAssetName.mobile.uyo_mask_legacy;
  const composedAssetDimensions = screen === 'mw' ? [372, 372] : [600, 600];
  const assetLegacyDimensions = screen === 'mw' ? [250, 340] : [500, 680];
  const uyoMaskAssetKey =
    screen === 'mw' ? GalleryAssetName.desktop.uyo_mask : GalleryAssetName.mobile.uyo_mask;
  const isCard = window.cards_page || (result.usedAs && result.usedAs !== 'invitation');
  const isVideo = isAllowedVideoFormat(result.src);
  // This is the uploaded gallery image from the user
  const {getCachedMediaDataById} = useCachedMedia(GALLERY_PHOTO_PREVIEW_INDEXED_DB_STORE_NAME);
  const {isInUyoOrDyo} = useUyoOrDyo();
  const media = getCachedMediaDataById(GALLERY_PHOTO_PREVIEW_MEDIA_ID);
  const hasMedia = !!media;
  const isNewOrientation = !!result.assets?.default[GalleryAssetName.desktop.standalone];
  const dispatch = useDispatch();
  const in3AcrossABTest = isIn3AcrossABTest();
  const getUyoDyoImageStyles = (uyoImagePosition) => {
    if (!Object.keys(uyoImagePosition).length) {
      return {
        position: 'absolute',
        width: '100%',
        height: '100%',
        objectFit: 'cover',
      };
    }

    const {left, top, width, height, scaleX, scaleY} = uyoImagePosition;
    const templateWidth = result.width ?? DEFAULT_CANVAS_WIDTH;
    const templateHeight = result.height ?? DEFAULT_CANVAS_HEIGHT;

    return {
      position: 'absolute',
      left: `${(left / templateWidth) * 100}%`,
      top: `${(top / templateHeight) * 100}%`,
      width: `${((width * scaleX) / templateWidth) * 100}%`,
      height: `${((height * scaleY) / templateHeight) * 100}%`,
      objectFit: 'cover',
    };
  };
  const getTemplatePositionPercent = (result) =>
    in3AcrossABTest ? result.templatePositionPercentVariant : result.templatePositionPercent;
  const getTemplateImageStyles = (templatePositionPercent) => {
    if (!templatePositionPercent || !Object.keys(templatePositionPercent).length) {
      return {
        position: 'absolute',
        width: '100%',
        height: '100%',
        objectFit: 'cover',
      };
    }
    return {
      position: 'absolute',
      left: `${templatePositionPercent.left}%`,
      top: `${templatePositionPercent.top}%`,
      width: `${templatePositionPercent.width}%`,
      height: `${templatePositionPercent.height}%`,
      overflow: 'hidden',
    };
  };
  const renderImageOrVideo = (displayName, categoryId, src, templateId, className) => {
    const [width, height] =
      result.premium || isNewOrientation ? composedAssetDimensions : assetLegacyDimensions;
    const assetId = `gallery-template__${templateId}__${isVideo ? 'video' : 'image'}`;
    const dataQaId = `gallery-template__${isVideo ? 'video' : 'image'}`;
    return isVideo ? (
      <Video
        src={src}
        className={className}
        id={assetId}
        data-qa-id={dataQaId}
        width={width}
        height={height}
        inViewAutoPlay
      />
    ) : (
      <Picture
        width={width}
        height={height}
        srcSet={src}
        className={className}
        alt={`${displayName}${categoryId?.trim() ? ` - ${categoryId}` : ''} - ${isCard ? 'Card' : 'Invitation'}`}
        id={assetId}
        data-qa-id={dataQaId}
        loading={loading}
      />
    );
  };

  const renderRegularTemplateAsset = () =>
    renderImageOrVideo(
      result.displayName,
      result.categoryId,
      result.src,
      result.templateId,
      'gallery-template__design--template-precomposed'
    );
  const renderUyoDyoContainer = () => (
    <>
      <div
        data-qa-id="gallery-template__image"
        id={`gallery-template__${result.templateId}__`}
        style={getTemplateImageStyles(getTemplatePositionPercent(result))}
      >
        <img
          data-qa-id="gallery-template__user-upload"
          id={`gallery-template__${result.templateId}__image`}
          src={media}
          style={getUyoDyoImageStyles(result.uyoImagePosition)}
          alt=""
        />
      </div>
      <img
        data-qa-id="gallery-template__image"
        id={`gallery-template__${result.templateId}__uyo-mask-image`}
        style={getTemplateImageStyles(getTemplatePositionPercent(result))}
        src={
          result.assets?.default[uyoMaskAssetKey] || result.assets?.default[uyoMaskAssetKeyLegacy]
        }
        alt=""
      />
    </>
  );

  const renderCompositeUyoDyoTemplatePremium = () => (
    <div
      className={
        in3AcrossABTest
          ? 'gallery-template__design--card-premium-3-across-variant'
          : 'gallery-template__design--card-premium'
      }
      style={{
        backgroundImage:
          result.src && !isAllowedVideoFormat(result.src) ? `url(${result.src})` : undefined,
      }}
    >
      {isAllowedVideoFormat(result.src) && (
        <Video src={result.src} className="gallery-template__design--template-precomposed" />
      )}
      {renderUyoDyoContainer()}
    </div>
  );

  const renderCompositeUyoDyoTemplateCard = () => (
    <div className="gallery-template__design--greeting-card">
      <div
        data-qa-id="gallery-template__image"
        id={`gallery-template__${result.templateId}__`}
        className="gallery-template__design--template-img-greeting-card"
      >
        <img
          data-qa-id="gallery-template__user-upload"
          id={`gallery-template__${result.templateId}__image`}
          src={media}
          style={getUyoDyoImageStyles(result.uyoImagePosition)}
          alt=""
        />
      </div>
      <img
        data-qa-id="gallery-template__image"
        id={`gallery-template__${result.templateId}__uyo-mask-image`}
        className="gallery-template__design--template-img-greeting-card"
        src={
          result.assets?.default[uyoMaskAssetKey] || result.assets?.default[uyoMaskAssetKeyLegacy]
        }
        alt=""
      />
    </div>
  );

  const renderTemplateAsset = () => {
    const isBaseUyoDyoTemplate = (result.uyo || result.dyo) && !result.templateId.endsWith('_uyol');
    if (!result.uyoImagePosition || !hasMedia || !isInUyoOrDyo) {
      return renderRegularTemplateAsset();
    }
    if (!isCard) {
      if (isBaseUyoDyoTemplate) {
        return renderCompositeUyoDyoTemplatePremium();
      }
      return renderRegularTemplateAsset();
    }
    return renderCompositeUyoDyoTemplateCard();
  };
  const premiumOrFreePrefix = result.premium ? 'premium' : 'free';
  const templateType =
    window.cards_page || (result.usedAs && result.usedAs !== 'invitation') ? 'card' : 'invitation';

  return (
    <div
      className={cx(
        'gallery-template__design',
        `gallery-template__design__${premiumOrFreePrefix}__${templateType}`,
        {
          'gallery-template__design__3-across-ABTest': in3AcrossABTest,
        }
      )}
      data-qa-id="gallery-template__design"
      ref={ref}
    >
      <QuickViewButton
        onClickQuickView={onClickQuickView}
        templateId={result.templateId}
        usedAs={result.usedAs}
      />
      <a
        data-qa-id="gallery-template__design__cta"
        href={href}
        data-ecom-category={`${result.premium ? 'Premium' : 'Free'}/${
          isCard ? 'Card' : 'Invitation'
        }/${result.categoryId}`}
        data-ecom-name={result.displayName}
        data-ecom-id={result.templateId}
        data-ecom-variant={result.versionName || ''}
        data-ecom-position={result.position}
        className="gallery-template__design--wrapper"
        onClick={
          onTemplateClick
            ? (e) => {
                e.preventDefault();
                e.stopPropagation();
                onTemplateClick(e, result.templateId, href, 'template');
              }
            : undefined
        }
      >
        {inView ? (
          <div
            className={`gallery-template__result-img-wrapper ${result.premium ? 'premium' : ''}`}
            style={{
              position: 'relative',
              boxShadow: !window.cards_page ? 'none' : undefined,
            }}
          >
            {renderTemplateAsset()}
            {result.badge && <Badge text={result.badge} />}
          </div>
        ) : (
          <Skeleton style={{height: '200px'}} />
        )}
      </a>

      {in3AcrossABTest && (
        <FavoritesHeart
          identifier={result.templateId}
          variant
          onClick={() => {
            dispatch(
              favoriteTemplateThunk(
                result.templateId,
                result.premium,
                favorited ? 'delete' : 'post'
              )
            );
          }}
          favorited={favorited}
        />
      )}
    </div>
  );
};

TemplateDesign.propTypes = {
  result: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.array,
      PropTypes.object,
      PropTypes.number,
      PropTypes.bool,
    ])
  ).isRequired,
  href: PropTypes.string.isRequired,
  onTemplateClick: PropTypes.func,
};

export const TemplateDetails = (props) => {
  const {result, screen, onTemplateClick, href, favorited = false} = props;
  const dispatch = useDispatch();
  const {matches: largeScreen} = useMatchQuery('(min-width: 80rem)');
  const mobileFabricCls = screen === 'mw' ? ' mobilefabric' : '';
  const showArtist = evite.features.isEnabled('SHOW_ARTIST_IN_GALLERY');
  const premiumOrFreePrefix = result.premium ? 'premium' : 'free';
  const templateType =
    window.cards_page || (result.usedAs && result.usedAs !== 'invitation') ? 'card' : 'invitation';
  const in3AcrossABTest = isIn3AcrossABTest();

  return (
    <div
      className={cx(
        `gallery-template__desc__${premiumOrFreePrefix}__${templateType}`,
        mobileFabricCls
      )}
      data-qa-id="gallery-template__details"
    >
      <TemplateTypeIndicator
        isPrem={result.premium}
        isGc={result.premium && result.usedAs && result.usedAs !== 'invitation'}
        dataQaValue={result.templateId}
        onClick={
          result.premium && onTemplateClick
            ? (e) => {
                e.preventDefault();
                e.stopPropagation();
                onTemplateClick(e, result.templateId, href, 'type-indicator');
              }
            : undefined
        }
      />
      <div className="gallery-template__title">
        <a
          href={href}
          data-qa-id="gallery-template__template-name__cta"
          data-ecom-category={`${result.premium ? 'Premium' : 'Free'}/${
            window.cards_page || result.usedAs !== 'invitation' ? 'Card' : 'Invitation'
          }/${result.categoryId}`}
          data-ecom-name={result.displayName}
          data-ecom-id={result.templateId}
          data-ecom-variant={result.versionName || ''}
          data-ecom-position={result.position}
          onClick={
            onTemplateClick
              ? (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onTemplateClick(e, result.templateId, href, 'template');
                }
              : undefined
          }
        >
          <Typography
            variant={largeScreen ? 'paragraph1' : 'paragraph3'}
            className="gallery-template__title-text"
          >
            {result.displayName}
          </Typography>
        </a>
        {!in3AcrossABTest && (
          <FavoritesHeart
            identifier={result.templateId}
            onClick={() => {
              dispatch(
                favoriteTemplateThunk(
                  result.templateId,
                  result.premium,
                  favorited ? 'delete' : 'post'
                )
              );
            }}
            favorited={favorited}
          />
        )}
      </div>
      {showArtist && result.artist && (
        <Typography
          className="gallery-template__artist"
          variant="paragraph3"
          color="coin"
          data-qa-id="gallery-template_artist-name"
        >
          by {result.artist}
        </Typography>
      )}
    </div>
  );
};

TemplateDetails.propsTypes = {
  result: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.array,
      PropTypes.object,
      PropTypes.number,
      PropTypes.bool,
    ])
  ).isRequired,
  favorited: PropTypes.bool,
  screen: PropTypes.oneOf(['mw', 'desktop']).isRequired,
  href: PropTypes.string.isRequired,
  onTemplateClick: PropTypes.func,
};

const Badge = ({text = ''}) => {
  const {matches: lg} = useMatchQuery('(min-width: 80rem)');

  return (
    text && (
      <Typography
        variant={`paragraph${lg ? 3 : 4}`}
        color="charcoal"
        className="gallery-template__social-proof-badge"
      >
        {text}
      </Typography>
    )
  );
};
